import { useState, useEffect } from "react";
import { roleLabel } from "@/lib/roleLabel";
import CentralStatusIndicator from "@/components/pos/CentralStatusIndicator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutGrid, Package, BarChart3, Settings, LogOut, Tag, Menu, X, Monitor, Boxes, Home, Clock, FolderOpen, Calculator, Ticket } from "lucide-react";
import { APP_VERSION } from "@/version";
import axios from "axios";
import { API } from "@/apiConfig";
import { usePermissions } from "@/hooks/usePermissions";
import { brandLogo } from "@/lib/brand";

export default function Navbar({ employee, onLogout, displaySettings, station, isFullscreen, onToggleFullscreen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMainPOS = location.pathname === "/pos";
  const [menuOpen, setMenuOpen] = useState(false);
  const stationName = station?.station_name || localStorage.getItem("station_name") || "";
  const [now, setNow] = useState(new Date());
  const [storeName, setStoreName] = useState("");
  const [lotteryEnabled, setLotteryEnabled] = useState(() => localStorage.getItem("lottery_enabled_cache") === "1");
  const [lotteryOnly, setLotteryOnly] = useState(() => localStorage.getItem("lottery_only_cache") === "1");
  const [storeId, setStoreId] = useState("");
  const [terminalId, setTerminalId] = useState("");
  const [centralConnected, setCentralConnected] = useState(null); // null = not central-managed
  // V10.54 — the floating Minimize/Maximize window buttons (App.js `MinimizeAppButton`, fixed
  // top-2 right-2) only render on Electron kiosk terminals (Lottery-Only or Backoffice) — same
  // two conditions it's shown under. Reserve room on the right so Store/Terminal, role badge and
  // Logout never sit underneath those buttons.
  const backofficeTerminal = localStorage.getItem("backoffice_terminal") === "1";
  const reserveForWindowControls = !!window.electronAPI?.isElectron && (lotteryOnly || backofficeTerminal);

  useEffect(() => {
    const api = window.electronAPI;
    if (!api) return;
    api.getConfig().then((cfg) => {
      if (cfg) {
        setStoreId(cfg.store_id || "");
        setTerminalId(cfg.register_id || "");
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Use the same reachability check as the blue/red CentralStatusIndicator square below —
    // previously this polled /settings/central-sync-status, a narrower mobile-bridge-push
    // diagnostic, which could show red even while Central was actually reachable (confusing:
    // blue dot = online, but Store/Terminal text = red, at the same time).
    const checkCentralStatus = () => {
      axios.get(`${API}/settings/central-status`).then((r) => {
        const s = r.data || {};
        if (!s.central_managed) { setCentralConnected(null); return; }
        setCentralConnected(!!s.online);
      }).catch(() => setCentralConnected(false));
    };
    checkCentralStatus();
    const timer = setInterval(checkCentralStatus, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    axios.get(`${API}/settings/store`).then(r => setStoreName(r.data?.store_name || "")).catch(() => {});
    const pollLottery = () => {
      axios.get(`${API}/lottery/settings/public`).then(r => {
        setLotteryEnabled(!!r.data?.enabled);
        setLotteryOnly(!!r.data?.lottery_only);
        localStorage.setItem("lottery_enabled_cache", r.data?.enabled ? "1" : "0");
        localStorage.setItem("lottery_only_cache", r.data?.lottery_only ? "1" : "0");
      }).catch(() => {});
    };
    pollLottery();
    const timer = setInterval(pollLottery, 60000);
    return () => clearInterval(timer);
  }, []);

  const isActive = (path) => location.pathname === path;

  const { can } = usePermissions(employee);

  const navLinks = lotteryOnly ? [] : [
    { key: "products", path: "/pos/products", label: "Products", icon: Package },
    { key: "inventory", path: "/pos/inventory", label: "Inventory", icon: Boxes },
    { key: "promotions", path: "/pos/promotions", label: "Promotions", icon: Tag },
    { key: "reports", path: "/pos/reports", label: "Reports", icon: BarChart3 },
    { key: "invoices", path: "/pos/invoices", label: "Invoices & Documents", icon: FolderOpen },
    { key: "accounting", path: "/pos/accounting", label: "Accounting", icon: Calculator },
    ...(lotteryEnabled ? [{ key: "lottery", path: "/pos/lottery", label: "Lottery", icon: Ticket }] : []),
    { key: "settings", path: "/pos/settings", label: "Settings", icon: Settings },
  ].filter((link) => can(link.key));

  return (
    <nav 
      className="border-b shadow-sm" 
      data-testid="navbar"
      style={{
        backgroundColor: displaySettings?.top_header_bg_color || "#FFFFFF",
        borderColor: displaySettings?.top_header_border_color || "#E4E4E7"
      }}
    >
      <div className="px-2 py-0.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 pl-10 shrink-0">
            <Link to="/pos" className="flex items-center" data-testid="navbar-logo-link">
              <img 
                src={brandLogo()} 
                alt="Modesk POS" 
                className="h-14 w-auto"
                data-testid="navbar-logo"
              />
            </Link>
            {/* Station Indicator */}
            {stationName && (
              <div
                className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm font-medium"
                data-testid="navbar-station-badge"
              >
                <Monitor className="w-4 h-4" />
                {stationName}
              </div>
            )}
            <span className="text-[12px] text-zinc-400" style={{ fontFamily: "'PT Sans Narrow', sans-serif" }}>Software: {APP_VERSION}</span>
          </div>
          {/* Center: Store Name + Date & Time + Store/Terminal ID */}
          <div className="flex-1 min-w-0 flex items-center justify-center gap-3 text-zinc-700 overflow-hidden px-2" data-testid="navbar-clock">
            {storeName && (
              <span className="text-black font-medium truncate max-w-[180px]" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 500 }} data-testid="navbar-store-name">{storeName}</span>
            )}
            <Clock className="w-4 h-4 text-zinc-400 shrink-0" />
            <span className="text-base font-semibold tracking-wide shrink-0">
              {now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            </span>
            <span className="text-base font-bold text-zinc-900 tabular-nums shrink-0">
              {now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          </div>
          <div className={`flex items-center gap-3 shrink-0 ${reserveForWindowControls ? "pr-24" : ""}`} data-testid="navbar-right-group">
            {/* Home Button — hidden in Lottery-Only mode: there's nowhere else to go */}
            {!lotteryOnly && location.pathname !== "/pos" && (
              <Link to="/pos" className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm font-medium hover:bg-blue-100 transition-all" data-testid="nav-home">
                <Home className="w-4 h-4" />
                Home
              </Link>
            )}
            {(storeId || terminalId) && (
              <div className="flex items-center gap-2 shrink-0">
              <CentralStatusIndicator compact />
              <div
                className={`flex flex-col leading-tight text-[14px] shrink-0 ${centralConnected === null ? "text-zinc-400" : centralConnected ? "text-emerald-600" : "text-red-500"}`}
                style={{ fontFamily: "'PT Sans Narrow', sans-serif" }}
                data-testid="navbar-store-terminal-id"
                title={centralConnected === null ? "Not Central-managed" : centralConnected ? "Connected to Central" : "Not connected to Central"}
              >
                {storeId && <span>Store: {storeId}</span>}
                {terminalId && <span>Terminal: {terminalId}</span>}
              </div>
              </div>
            )}
            <div className="text-right">
              <p className="text-sm font-bold text-zinc-900" data-testid="navbar-employee-name">{employee.name}</p>
              <p className="text-xs text-zinc-600" data-testid="navbar-role-label">{roleLabel(employee)}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              className="border-red-200 text-red-600 hover:bg-red-50 font-bold"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
            {/* Hamburger Menu — on the main POS screen this opens Control Center directly;
                on other pages it keeps the old quick-jump dropdown. Hidden entirely in
                Lottery-Only mode: there are no other pages to jump to. */}
            {!lotteryOnly && (
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => (isMainPOS ? navigate("/pos/control-center") : setMenuOpen(!menuOpen))}
                className="p-2"
                data-testid="menu-toggle"
              >
                {menuOpen && !isMainPOS ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              {menuOpen && !isMainPOS && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-zinc-200 rounded-lg shadow-xl z-50">
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 font-medium transition-all ${
                          isActive(link.path)
                            ? "bg-blue-50 text-blue-600"
                            : "text-zinc-700 hover:bg-zinc-50"
                        }`}
                        data-testid={`nav-${link.label.toLowerCase()}`}
                      >
                        <Icon className="w-4 h-4" />
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
